import {
  BattleCancelledPayload,
  BattleCardView,
  BattleResultPayload,
  BattleSideReveal,
  BattleStartedPayload,
  CardPlacedPayload,
  DuelPhase,
} from "@/types/battle"
import {
  mergeCardShardSlots,
  normalizeBattleCardView,
} from "@/utils/battleCards"

function resolvePlacedCard(
  payload: CardPlacedPayload,
  yourCards: BattleCardView[] | undefined
): BattleCardView {
  const normalized =
    normalizeBattleCardView(payload.card) ?? payload.card
  const fromHand = yourCards?.find((card) => card.id === normalized.id)
  if (!fromHand) return normalized
  return mergeCardShardSlots(
    {
      ...fromHand,
      ...normalized,
    },
    fromHand
  )
}

export type DuelAction =
  | { type: "RESET" }
  | { type: "START_MATCHMAKING" }
  | { type: "BATTLE_STARTED"; payload: BattleStartedPayload }
  | { type: "CARD_PLACED"; payload: CardPlacedPayload }
  | { type: "OPPONENT_SELECTED"; payload: { opponentReady: boolean } }
  | { type: "START_REVEAL"; payload: BattleResultPayload }
  | { type: "REVEAL_DONE" }
  | { type: "BATTLE_CANCELLED"; payload: BattleCancelledPayload }
  | { type: "TICK_TIMER" }

export interface DuelState {
  phase: DuelPhase
  battle: BattleStartedPayload | null
  yourSlot: "A" | "B" | null
  yourCard: BattleCardView | null
  opponentReady: boolean
  result: BattleResultPayload | null
  yourSide: BattleSideReveal | null
  opponentSide: BattleSideReveal | null
  cancelled: BattleCancelledPayload | null
  timerSec: number
  selectionDeadlineAt: number | null
}

export const initialDuelState: DuelState = {
  phase: "idle",
  battle: null,
  yourSlot: null,
  yourCard: null,
  opponentReady: false,
  result: null,
  yourSide: null,
  opponentSide: null,
  cancelled: null,
  timerSec: 0,
  selectionDeadlineAt: null,
}

export function duelReducer(state: DuelState, action: DuelAction): DuelState {
  switch (action.type) {
    case "RESET":
      return { ...initialDuelState }

    case "START_MATCHMAKING":
      return { ...initialDuelState, phase: "matchmaking" }

    case "BATTLE_STARTED": {
      const raw = action.payload as BattleStartedPayload & {
        selectionDeadlineAt?: string | number
        cardSelectionDeadlineAt?: string | number
      }
      let deadline =
        Date.now() + action.payload.cardSelectionTimeoutSec * 1000
      const serverDeadline =
        raw.selectionDeadlineAt ?? raw.cardSelectionDeadlineAt
      if (serverDeadline !== undefined) {
        const ts =
          typeof serverDeadline === "string"
            ? Date.parse(serverDeadline)
            : serverDeadline
        if (!Number.isNaN(ts)) deadline = ts
      }

      return {
        ...state,
        phase: "selecting",
        battle: action.payload,
        timerSec: Math.max(0, Math.ceil((deadline - Date.now()) / 1000)),
        selectionDeadlineAt: deadline,
        opponentReady: false,
        yourCard: null,
        yourSlot: null,
      }
    }

    case "CARD_PLACED": {
      const placedCard = resolvePlacedCard(
        action.payload,
        state.battle?.yourCards
      )
      const isYourCard = state.battle?.yourCards.some(
        (card) => card.id === placedCard.id
      )

      if (!isYourCard) {
        return {
          ...state,
          opponentReady: action.payload.opponentReady,
          phase: state.yourCard
            ? action.payload.opponentReady
              ? "opponent_ready"
              : "card_placed"
            : "selecting",
        }
      }

      return {
        ...state,
        phase: action.payload.opponentReady ? "opponent_ready" : "card_placed",
        yourSlot: action.payload.slot,
        yourCard: placedCard,
        opponentReady: action.payload.opponentReady,
      }
    }

    case "OPPONENT_SELECTED":
      return {
        ...state,
        opponentReady: action.payload.opponentReady,
        phase:
          state.yourCard && action.payload.opponentReady
            ? "opponent_ready"
            : state.yourCard
              ? "card_placed"
              : "selecting",
      }

    case "START_REVEAL":
      return {
        ...state,
        phase: "revealing",
        result: action.payload,
      }

    case "REVEAL_DONE":
      return { ...state, phase: "result" }

    case "BATTLE_CANCELLED":
      return {
        ...state,
        phase: "cancelled",
        cancelled: action.payload,
      }

    case "TICK_TIMER": {
      if (
        state.phase !== "selecting" &&
        state.phase !== "card_placed" &&
        state.phase !== "opponent_ready"
      ) {
        return state
      }
      if (!state.selectionDeadlineAt) return state

      const remaining = Math.max(
        0,
        Math.ceil((state.selectionDeadlineAt - Date.now()) / 1000)
      )

      if (remaining === 0 && state.phase === "selecting" && !state.yourCard) {
        return {
          ...state,
          timerSec: 0,
          phase: "cancelled",
          cancelled: {
            battleId: state.battle?.battleId ?? "",
            mode: state.battle?.mode ?? "RADAR",
            reason: "CARD_SELECTION_TIMEOUT",
            message: "Время выбора карты истекло",
            energyRefunded: true,
          },
        }
      }

      if (remaining === state.timerSec) return state
      return { ...state, timerSec: remaining }
    }

    default:
      return state
  }
}
